import { FunctionComponent, SVGProps } from "react";
import { ReactComponent as IconStar } from "@Icons/16x16/star.svg";
import { ReactComponent as IconTimer } from "@Icons/16x16/timer.svg";
import {
  CheerzApiAction,
  CheerzApiBackgroundColor,
  CheerzApiCta,
  CheerzApiImage,
  CheerzApiLabel,
  CheerzApiMetadata,
  CheerzApiPageResponse,
  CheerzApiSection,
  CheerzApiTextBlock,
  CheerzApiTile,
} from "@Types/api/pagify";
import { PriceFormatted as PriceFormattedApi } from "@Types/api/price";
import {
  Action,
  BackgroundColor,
  Cta,
  Image,
  Label,
  Metadata,
  NavigationLink,
  Page,
  Section,
  TextBlock,
  Tile,
} from "@Types/pagify";
import { PriceFormatted } from "@Types/price";

export const convertPageFromApiToContent = (jsonContent: CheerzApiPageResponse): Page => {
  const sections: Section[] = jsonContent.page.sections.map((section) =>
    convertSectionFromApiToContent(section)
  );

  const tiles: Tile[] = jsonContent.tiles.map((tile) => convertTileFromApiToContent(tile));

  const page: Page = {
    header: {
      displayType: jsonContent.page.header.display_type,
      text: jsonContent.page.header.text,
    },
    metadata: convertMetadataFromApiToContent(jsonContent.page.metadata_tmp),
    sections: sections,
    slug: jsonContent.page.slug,
    tiles: tiles,
  };

  return page;
};

const convertSectionFromApiToContent = (section: CheerzApiSection): Section => {
  return {
    backgroundColor:
      section.background_color && convertBackgroundColorFromApiToContent(section.background_color),
    cta: section.cta.map((cta) => convertCtaFromApiToContent(cta)),
    description: section.description && convertTextBlockFromApiToContent(section.description),
    displayType: section.display_type,
    metadata: section.metadata_tmp ? convertMetadataFromApiToContent(section.metadata_tmp) : null,
    subtitle: section.subtitle && convertTextBlockFromApiToContent(section.subtitle),
    tileSlugs: section.tile_slugs,
    title: section.title,
  };
};

const convertTileFromApiToContent = (tile: CheerzApiTile): Tile => {
  return {
    action: tile.action && convertActionFromApiToContent(tile.action),
    backgroundColor:
      tile.background_color && convertBackgroundColorFromApiToContent(tile.background_color),
    cta: !!tile.cta ? convertCtaFromApiToContent(tile.cta) : null,
    images: tile.images.map((image) => convertImageFromApiToContent(image)),
    label: tile.label && convertLabelFromApiToContent(tile.label),
    metadata: convertMetadataFromApiToContent(tile.metadata_tmp),
    navigationLink: tile.navigation_link,
    slug: tile.slug,
    textBlock1:
      tile.text_block_1 && tile.text_block_1.length > 0
        ? convertTextBlockFromApiToContent(tile.text_block_1)
        : null,
    textBlock2:
      tile.text_block_2 && tile.text_block_2.length > 0
        ? convertTextBlockFromApiToContent(tile.text_block_2)
        : null,
    textBlock3:
      tile.text_block_3 && tile.text_block_3.length > 0
        ? convertTextBlockFromApiToContent(tile.text_block_3)
        : null,
    textBlock4:
      tile.text_block_4 && tile.text_block_4.length > 0
        ? convertTextBlockFromApiToContent(tile.text_block_4)
        : null,
  };
};

const convertImageFromApiToContent = (image: CheerzApiImage): Image => {
  return {
    desktop: {
      "1x": image.desktop["1x"],
      "2x": image.desktop["2x"],
    },
    webMobile: image.web_mobile && {
      "1x": image.web_mobile["1x"],
      "2x": image.web_mobile["2x"],
    },
  };
};

const convertTextBlockFromApiToContent = (jsonContent: CheerzApiTextBlock[]): TextBlock[] => {
  return jsonContent.map((textBlock) => {
    return {
      navigationLink: textBlock.navigation_link ? textBlock.navigation_link : null,
      style: textBlock.style,
      text: textBlock.text,
    };
  });
};

const convertCtaFromApiToContent = (cta: CheerzApiCta): Cta => {
  return {
    navigationLink: cta.navigation_link,
    text: cta.text,
  };
};

const convertBackgroundColorFromApiToContent = (
  background_color: CheerzApiBackgroundColor
): BackgroundColor => {
  return {
    hex: background_color.hex,
    opacityPercentage: background_color.opacity_percentage,
  };
};

const convertLabelFromApiToContent = (label: CheerzApiLabel): Label => {
  return {
    color: {
      hex: label.color.hex,
      opacityPercentage: label.color.opacity_percentage,
    },
    labelType: label.label_type,
    text: label.text,
  };
};

const convertActionFromApiToContent = (action: CheerzApiAction): Action => {
  return {
    displayType: action.display_type,
    entries: action.entries
      ? action.entries.map((entry) => {
          return {
            custoInfo: {
              kustomVersion: entry.custo_info.kustom_version,
            },
            discount: entry.discount,
            fullPrice: entry.web_full_price,
            navigationLink: entry.navigation_link ? entry.navigation_link : null,
            price: entry.price,
            text: entry.text,
          };
        })
      : null,
    subtitle: action.subtitle,
    title: action.title,
  };
};

const convertMetadataFromApiToContent = (metadata: CheerzApiMetadata | null): Metadata | null => {
  return metadata && Object.keys(metadata).length !== 0
    ? {
        ...(!!metadata.availability && {
          availability: metadata.availability,
        }),
        ...(!!metadata.delivery_days_range && {
          deliveryDaysRange: metadata.delivery_days_range,
        }),
        ...(!!metadata.kind && {
          kind: metadata.kind,
        }),
        ...(!!metadata.kustom_version && {
          kustomVersion: metadata.kustom_version,
        }),
        ...(metadata.kustom_web_mobile !== null &&
          metadata.kustom_web_mobile !== undefined && {
            kustomWebMobile: metadata.kustom_web_mobile,
          }),
        ...(!!metadata.last_order_date && {
          lastOrderDate: {
            backgroundColor: metadata.last_order_date.background_color,
            iconBackgroundColor: metadata.last_order_date.icon_background_color,
            message: metadata.last_order_date.message,
            textColor: metadata.last_order_date.text_color,
            type: metadata.last_order_date.type,
          },
        }),
        ...(!!metadata.meta_description && {
          metaDescription: metadata.meta_description,
        }),
        ...(!!metadata.meta_title && {
          metaTitle: metadata.meta_title,
        }),
        ...(!!metadata.products && {
          products: metadata.products.map((product) => {
            return {
              availability: product.availability,
              kustomVersion: product.kustom_version,
              tag: product.tag,
            };
          }),
        }),
        ...(!!metadata.product_tag && {
          productTag: metadata.product_tag,
        }),
        ...(!!metadata.rating_value && {
          ratingValue: metadata.rating_value,
        }),
        ...(!!metadata.review_count && {
          reviewCount: metadata.review_count,
        }),
        ...(!!metadata.shipping_prices && {
          shippingPrices: metadata.shipping_prices.map((option) => {
            const expressOption = option["express"];
            const relayPointOption = option["relay_point"];
            const standardOption = option["standard"];

            return {
              ...(!!expressOption && {
                express: getshippingPricesMetadata(expressOption),
              }),
              ...(!!relayPointOption && {
                relayPoint: getshippingPricesMetadata(relayPointOption),
              }),
              ...(!!standardOption && {
                standard: getshippingPricesMetadata(standardOption),
              }),
            };
          }),
        }),
        ...(!!metadata.web && {
          web: {
            ctaText: metadata.web.cta_text || null,
            productDescription: metadata.web.product_description || null,
            products: metadata.web.products
              ? metadata.web.products.map((choice) => {
                  return {
                    productName: choice.product_name,
                    productPrice: choice.product_price,
                    productTag: choice.product_tag,
                    products: choice.products
                      ? choice.products.map((entry) => {
                          return {
                            productName: entry.product_name,
                            productPrice: entry.product_price,
                            productTag: entry.product_tag,
                            withQuantity: entry.with_quantity,
                          };
                        })
                      : null,
                    withQuantity: choice.with_quantity,
                  };
                })
              : null,
          },
        }),
      }
    : null;
};

export const getshippingPricesMetadata = (price: PriceFormattedApi): PriceFormatted => {
  return {
    cents: price.cents,
    currency: price.currency,
    formattedPrice: price.formatted_price,
  };
};

export const getLink = (
  navigationLink: NavigationLink | null,
  localeSlug: string
): string | undefined => {
  if (!navigationLink || !navigationLink?.link) {
    return undefined;
  }
  if (navigationLink.type === "url") {
    return navigationLink.link;
  }
  if (navigationLink.type === "page") {
    return convertSlugToUrl(navigationLink.link, localeSlug);
  }
  if (
    navigationLink.type === "legacy_customization" ||
    navigationLink.type === "kustom_customization"
  ) {
    return `/${localeSlug}/articles/${navigationLink?.link}`;
  }
  if (navigationLink.type === "path") {
    if (navigationLink.link.charAt(0) === "/") {
      return `/${localeSlug}${navigationLink.link}`;
    }
    return `/${localeSlug}/${navigationLink.link}`;
  }
};

export const convertSlugToUrl = (slug: string, localeSlug: string): string | undefined => {
  const regex = new RegExp("(category|collection|product|choice)_page_web-(.*)");
  const [, pageType, pageSlug] = regex.exec(slug) || [];

  let url;
  switch (pageType) {
    case "category":
      url = `/${localeSlug}/categories/${pageSlug}`;
      break;
    case "collection":
      url = `/${localeSlug}/collections/${pageSlug}`;
      break;
    case "product":
      url = `/${localeSlug}/products/${pageSlug}`;
      break;
    case "choice":
      url = `/${localeSlug}/products/${pageSlug}/choices`;
      break;
    default:
      url = undefined;
      throw new Error(`getPathFromSlug : Could not find page url with slug : ${slug}`);
  }

  return url;
};

export const getLabelIcon = (type: string): FunctionComponent<SVGProps<SVGSVGElement>> | null => {
  let LabelIcon;
  switch (type) {
    case "classic":
      LabelIcon = null;
      break;
    case "lod":
      LabelIcon = IconTimer;
      break;
    case "starred":
      LabelIcon = IconStar;
      break;
    default:
      LabelIcon = null;
  }
  return LabelIcon;
};
